import { useEffect, useState } from "react";
import HaloWordmark from "@/components/HaloWordmark";
import MahanoTickMark from "@/components/MahanoTickMark";

/**
 * Cold-load splash. Shows a full-screen Mahano ring + wordmark for ~1.6s on the
 * first mount of a browsing session, then fades out. Uses sessionStorage so
 * client-side navigations, refreshes within the same tab, and hot-reloads
 * don't re-trigger it after the initial reveal.
 */
export default function HaloSplash() {
  const SEEN_KEY = "halo_splash_seen";
  const alreadySeen =
    typeof window !== "undefined" &&
    window.sessionStorage?.getItem(SEEN_KEY) === "1";
  const [visible, setVisible] = useState(!alreadySeen);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;
    const fadeTimer = setTimeout(() => setFading(true), 1400);
    const removeTimer = setTimeout(() => {
      setVisible(false);
      try {
        window.sessionStorage.setItem(SEEN_KEY, "1");
      } catch (err) {
        console.warn("sessionStorage unavailable for splash flag", err);
      }
    }, 1900);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="halo-splash"
      aria-hidden="true"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background transition-opacity duration-500"
      style={{ opacity: fading ? 0 : 1, pointerEvents: fading ? "none" : "auto" }}
    >
      <div style={{ animation: "halo-splash-rise 900ms cubic-bezier(0.16, 1, 0.3, 1) both" }}>
        <MahanoTickMark size={104} />
      </div>
      <div
        className="mt-7 text-foreground"
        style={{ animation: "halo-splash-fade 700ms 500ms cubic-bezier(0.16, 1, 0.3, 1) both" }}
      >
        <HaloWordmark height={22} />
      </div>
      <style>{`
        @keyframes halo-splash-rise {
          from { opacity: 0; transform: translateY(6px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes halo-splash-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
